import type {
  CollectionReference,
  DocumentData,
  FirestoreDataConverter,
  Query,
  QuerySnapshot,
  Unsubscribe,
} from 'firebase/firestore'
import { onSnapshot } from 'firebase/firestore'
import type { PageName } from '~/config'
import { Service } from './service'
import type { Controller } from '~/features/controller'
import { ControllerStateArray } from '~/features/controllerStateArray'
import { ControllerStateRecord } from '~/features/controllerStateRecord'
import type { FirestoreType } from '~/models/firestoreType'
import type { LocalStateChange } from '~/features/stateChange'

export abstract class LiveService<
  DataType extends FirestoreType,
  ReturnType extends DataType[] | Record<string, DataType>,
> extends Service<DataType, ReturnType> {
  protected readonly MAX_IN_CLAUSE = 30
  protected cachedData: Map<number, ReturnType> = new Map()
  protected cachedPage: string = ''
  protected unsubscribes: Unsubscribe[] = []
  protected lastQueries: Map<PageName, Query[]>
  protected collection: CollectionReference
  protected collectionGroup?: Query<DataType, DocumentData>
  protected collectionConstructor: <DataType extends FirestoreType>(
    collectionName: string,
    ...pathSegments: string[]
  ) => CollectionReference<DataType, DocumentData>

  protected converter: FirestoreDataConverter<DataType, DocumentData>

  constructor(collectionName: string, args?: { collectionGroupName?: string }) {
    super()
    this.lastQueries = new Map()

    const { collection: collectionConstructor, collectionGroup } = useFirebase()
    this.collection = collectionConstructor(collectionName)

    if (args) {
      if (args.collectionGroupName) {
        this.collectionGroup = collectionGroup(args.collectionGroupName)
      }
    }

    this.collectionConstructor = collectionConstructor
    const { firestoreGenericConverter } = useFirebase()
    this.converter = firestoreGenericConverter<DataType>()
  }

  public override activatePage(pageName: PageName): void {
    this.activePage = pageName

    this.unsubscribeListeners()

    if (this.lastQueries.has(pageName)) {
      const queries = this.lastQueries.get(pageName)!
      this.listenGroup(queries)
    }

    for (const subscriber of this.subscribers) {
      subscriber.activatePage(pageName)
    }
  }

  protected listenGroup(queries: Query[]) {
    this.lastQueries.set(this.activePage, queries)
    this.unsubscribeListeners()
    this.unsubscribes = queries.map((query, index) => {
      return onSnapshot(query.withConverter(this.converter), (snapshot) => {
        /* v8 ignore next  */
        this.updateInternalState.bind(this)(snapshot, index)
      })
    })

    if (queries.length === 0) {
      for (const subscriber of this.subscribers) {
        subscriber.clear()
      }
    }
  }

  protected listen(query: Query): void {
    this.listenGroup([query])
  }

  public stopListening(): void {
    this.unsubscribeListeners()

    for (const subscriber of this.subscribers) {
      subscriber.setLoading()
    }
  }

  public setLocalState(stateChanges: LocalStateChange<DataType>[]): void {
    for (const subscriber of this.subscribers) {
      subscriber.setLocalState(stateChanges)
    }
  }

  protected updateInternalState(
    snapshot: QuerySnapshot<DataType, DocumentData>,
    index: number = 0,
  ): void {
    const newState = this.parseToReturnType(snapshot, index)
    const amountOfQueries = this.lastQueries.get(this.activePage)?.length

    if (
      this.cachedData.size !== amountOfQueries
      && amountOfQueries !== undefined
    ) {
      return
    }

    this.updateSubscriberStates(newState)
  }

  protected unsubscribeListeners(): void {
    for (const unsubscribe of this.unsubscribes) {
      unsubscribe()
    }

    this.cachedData.clear()
    this.unsubscribes = []
  }

  protected abstract parseToReturnType(
    snapshot: QuerySnapshot<DataType, DocumentData>,
    index: number
  ): ReturnType

  protected abstract clearSubscriberStates(): void
}

export class ArrayService<DataType extends FirestoreType> extends LiveService<
  DataType,
  DataType[]
> {
  override createControllerState(
    controller: Controller,
    sideEffect?: () => void,
  ): ControllerStateArray<DataType> {
    return new ControllerStateArray(controller, sideEffect)
  }

  protected parseToReturnType(
    snapshot: QuerySnapshot<DataType, DocumentData>,
    index: number,
  ): DataType[] {
    const newData = []

    for (const doc of snapshot.docs) {
      newData.push(doc.data())
    }

    this.cachedData.set(index, newData)

    return Array.from(this.cachedData.values()).flat()
  }

  public clearSubscriberStates(): void {
    for (const subscriber of this.subscribers) {
      subscriber.setState([])
    }
  }
}

export class MapService<DataType extends FirestoreType> extends LiveService<
  DataType,
  Record<string, DataType>
> {
  override createControllerState(
    controller: Controller,
    sideEffect?: () => void,
  ): ControllerStateRecord<DataType> {
    return new ControllerStateRecord(controller, sideEffect)
  }

  override parseToReturnType(
    snapshot: QuerySnapshot<DataType, DocumentData>,
    index: number,
  ): Record<string, DataType> {
    const newData: Record<string, DataType> = {}
    // Gather the data
    for (const doc of snapshot.docs) {
      newData[doc.id] = doc.data()
    }

    this.cachedData.set(index, newData)
    const combinedData: Record<string, DataType> = {}
    for (const data of this.cachedData.values()) {
      Object.assign(combinedData, data)
    }
    return combinedData
  }

  public clearSubscriberStates(): void {
    for (const subscriber of this.subscribers) {
      subscriber.setState({})
    }
  }
}
